import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, useMemo } from 'react'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import { formatUtcDateTime, isThisWeekUtc, utcStringToLocalDate } from '../utils/timezone'
import { fetchCalendarEvents, fetchCalendarEventClients, type CalendarServiceEvent, listConversations, getConversation, listClients, listActionItems } from '../api/crm'
import Loading from '../components/Loading'
import type { ActionItem } from '../types/ActionItem'

// Sub-components
import MorningView from '../components/home/MorningView'
import PreMeetingView from '../components/home/PreMeetingView'
import PostMeetingView from '../components/home/PostMeetingView'
import EndOfDayView from '../components/home/EndOfDayView'
import DefaultView from '../components/home/DefaultView'
import { ONBOARDING_STATUS_KEY } from '../components/onboarding/OnboardingView'

import '../style/views/HomeView.css'

type HomeViewProps = {
  firstName?: string
}

type ClientRecord = {
  id: string
  first_name?: string
  last_name?: string
  email?: string
  company?: string
  date_created?: string
  created_at?: string
}

type NewContactCard = {
  id: string
  name: string
  company?: string
  createdLabel: string
}

type DashboardContext = 'MORNING' | 'PRE_MEETING' | 'POST_MEETING' | 'END_OF_DAY' | 'DEFAULT'

type Conversation = {
  id: string
  title?: string
  date_created?: string
  summary?: string
  action_items?: ActionItem[]
  dates?: Array<{ id: string; title?: string; datetime_start?: string }>
  emails?: Array<{ subject?: string; body?: string }>
  clients?: Array<{ id: string; first_name?: string; last_name?: string; email?: string }>
}

const getEventTagTheme = (tags?: string[] | string | null): 'meeting' | 'reminder' | 'default' => {
  const list = Array.isArray(tags) ? tags : typeof tags === 'string' ? [tags] : []
  const normalized = list.map((tag) => tag.toLowerCase())
  if (normalized.some((tag) => tag.includes('reminder'))) return 'reminder'
  if (normalized.some((tag) => tag.includes('meeting'))) return 'meeting'
  return 'default'
}

function HomeView({ firstName }: HomeViewProps) {
  const name = firstName?.trim()
  const typedNameRef = useRef<HTMLSpanElement | null>(null)
  const nameCharCount = name ? Array.from(`${name} 👋`).length : 0
  const nameStyle = name
    ? ({ ['--name-chars' as string]: nameCharCount } as CSSProperties)
    : undefined
  const [weeklyCalls, setWeeklyCalls] = useState<number | null>(null)
  const [latestCallTime, setLatestCallTime] = useState<Date | null>(null)
  const [latestConversation, setLatestConversation] = useState<Conversation | null>(null)
  const [clientTotals, setClientTotals] = useState<{ total: number; newThisWeek: number } | null>(null)
  const [newContacts, setNewContacts] = useState<NewContactCard[] | null>(null)
  const [latestActionItems, setLatestActionItems] = useState<ActionItem[] | null>(null)
  const [recentDrafts, setRecentDrafts] = useState<Array<{ id: string; subject: string; body: string; date: string }> | null>(null)
  const [activeEventDetails, setActiveEventDetails] = useState<{
    clients: ClientRecord[]
    conversations: Conversation[]
  } | null>(null)
  const [eventSummary, setEventSummary] = useState<{
    today: Array<{ id: string; time: string; title: string; tagTheme: 'meeting' | 'reminder' | 'default'; start: Date; conversation_id?: string | null }>
    pendingCount: number
    weekCount: number
  } | null>(null)
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())

  // Adaptive Dashboard State
  const [demoContext, setDemoContext] = useState<DashboardContext | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())

  const isStatsReady = weeklyCalls !== null && clientTotals !== null && eventSummary !== null
  const showWeeklyCallGradient = isStatsReady && weeklyCalls > 0
  const weeklyCallDisplay = showWeeklyCallGradient ? weeklyCalls : 0

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const loadWeeklyCalls = async () => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) {
          setWeeklyCalls(0)
          return
        }
        if (!userId) setUserId(ensuredId)
        const data = await listConversations(ensuredId, { signal: controller.signal })

        const thisWeek = data.filter((item) => isThisWeekUtc(item.date_created))

        if (data.length > 0) {
          const sorted = data.toSorted((a, b) => {
             const aTime = a.date_created ? new Date(a.date_created).getTime() : 0
             const bTime = b.date_created ? new Date(b.date_created).getTime() : 0
             return bTime - aTime
          })

          if (sorted[0].id) {
            const detail = await getConversation(sorted[0].id, ensuredId, controller.signal)
            const conv = detail.conversation
            if (conv) {
              setLatestConversation({
                id: conv.id,
                title: conv.title,
                date_created: conv.date_created,
                summary: conv.summary,
                action_items: detail.action_items || detail.actionItems,
                dates: detail.dates,
                emails: detail.emails,
                clients: detail.clients
              })
              if (conv.date_created) {
                setLatestCallTime(utcStringToLocalDate(conv.date_created))
              }
            }
          }
          // Set count AFTER details are back so the adaptive logic has the latestCallTime ready
          setWeeklyCalls(thisWeek.length)
        } else {
          setWeeklyCalls(0)
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load weekly calls', error)
          setWeeklyCalls(0)
        }
      }
    }

    loadWeeklyCalls()
    return () => controller.abort()
  }, [userId])

  useEffect(() => {
    const controller = new AbortController()
    const loadClients = async () => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) {
          setClientTotals({ total: 0, newThisWeek: 0 })
          setNewContacts([])
          return
        }
        if (!userId) setUserId(ensuredId)
        const data = (await listClients(ensuredId, controller.signal)) as ClientRecord[]
        const newThisWeekItems = data
          .filter((item) => isThisWeekUtc(item.date_created || item.created_at))
          .sort((a, b) => {
            const aTime = utcStringToLocalDate(a.date_created || a.created_at)?.getTime() ?? 0
            const bTime = utcStringToLocalDate(b.date_created || b.created_at)?.getTime() ?? 0
            return bTime - aTime
          })
        const newThisWeek = newThisWeekItems.length
        const cards = newThisWeekItems.slice(0, 4).map((item) => {
          const nameStr = [item.first_name, item.last_name].filter(Boolean).join(' ').trim() || item.email || 'New contact'
          const createdLabel = formatUtcDateTime(item.date_created || item.created_at).date || 'Recently added'
          return {
            id: item.id,
            name: nameStr,
            company: item.company,
            createdLabel,
          }
        })
        setClientTotals({ total: data.length, newThisWeek })
        setNewContacts(cards)
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load clients', error)
          setClientTotals({ total: 0, newThisWeek: 0 })
          setNewContacts([])
        }
      }
    }

    loadClients()
    return () => controller.abort()
  }, [userId])

  useEffect(() => {
    const controller = new AbortController()
    const loadRecentDrafts = async () => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) {
          setRecentDrafts([])
          return
        }
        
        // Fetch list of conversations
        const data = await listConversations(ensuredId, { signal: controller.signal })

        // Filter for today and yesterday
        const now = new Date()
        const startOfYesterday = new Date(now)
        startOfYesterday.setDate(now.getDate() - 1)
        startOfYesterday.setHours(0, 0, 0, 0)
        
        const recentConvIds = data
          .flatMap(item => {
            const date = utcStringToLocalDate(item.date_created)
            return date && date >= startOfYesterday ? [item.id] : []
          })
          
        // Fetch details for each recent conversation to get emails
        const draftArrays = await Promise.all(
          recentConvIds.map(async (id) => {
            const detail = await getConversation(id, ensuredId, controller.signal)
            if (!detail.emails || detail.emails.length === 0) return []
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
            return detail.emails.map((email: any) => ({
              id,
              subject: email.subject || 'No Subject',
              body: email.body || '',
              date: detail.conversation?.date_created ?? ''
            }))
          })
        )
        const drafts = draftArrays.flat()
        setRecentDrafts(drafts)
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load recent drafts', error)
          setRecentDrafts([])
        }
      }
    }
    loadRecentDrafts()
    return () => controller.abort()
  }, [userId])

  useEffect(() => {
    const controller = new AbortController()
    const loadEvents = async () => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) {
          setEventSummary({ today: [], pendingCount: 0, weekCount: 0 })
          return
        }
        if (!userId) setUserId(ensuredId)
        const now = new Date()
        const startOfToday = new Date(now)
        startOfToday.setHours(0, 0, 0, 0)
        const tomorrow = new Date(startOfToday)
        tomorrow.setDate(startOfToday.getDate() + 1)
        const dayOfWeek = startOfToday.getDay()
        const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek
        const endOfWeek = new Date(startOfToday)
        endOfWeek.setDate(startOfToday.getDate() + daysUntilSunday)
        endOfWeek.setHours(23, 59, 59, 999)

        let parsed: Array<{ id: string; title?: string; startValue?: string; start: Date; tags?: string[] | string | null; conversation_id?: string | null }>

          const fetchedEvents = await fetchCalendarEvents({}, controller.signal)
          parsed = fetchedEvents
            .flatMap((e: CalendarServiceEvent) => {
              const start = utcStringToLocalDate(e.starts_at)
              if (!start) return []
              return [{
                id: e.id,
                title: e.title,
                startValue: e.starts_at,
                start,
                tags: e.provider === 'microsoft' ? 'outlook' : (e.provider ?? null),
                conversation_id: null,
              }]
            })

        const today = parsed
          .filter((event) => event.start >= startOfToday && event.start < tomorrow)
          .sort((a, b) => a.start.getTime() - b.start.getTime())
          .map((event) => {
            const title = (event.title || '').trim() || 'Untitled event'
            const { time } = formatUtcDateTime(event.startValue)
            return {
              id: event.id,
              title,
              time: time || '—',
              tagTheme: getEventTagTheme(event.tags),
              start: event.start,
              conversation_id: event.conversation_id
            }
          })

        const pendingCount = parsed.filter(
          (event) => event.start >= startOfToday && event.start <= endOfWeek,
        ).length

        const weekCount = parsed.filter((event) => isThisWeekUtc(event.startValue)).length

        setEventSummary({ today, pendingCount, weekCount })
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load events', error)
          setEventSummary({ today: [], pendingCount: 0, weekCount: 0 })
        }
      }
    }

    loadEvents()
    return () => controller.abort()
  }, [userId])

  useEffect(() => {
    const controller = new AbortController()
    const loadActionItems = async () => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) {
          setLatestActionItems([])
          return
        }
        if (!userId) setUserId(ensuredId)
        const data = await listActionItems(ensuredId, controller.signal)
        const latest = data
          .filter((item) => !item.done)
          .sort((a, b) => {
            const aTime = utcStringToLocalDate(a.date_created)?.getTime() ?? 0
            const bTime = utcStringToLocalDate(b.date_created)?.getTime() ?? 0
            return bTime - aTime
          })
          .slice(0, 6)
        setLatestActionItems(latest)
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load action items', error)
          setLatestActionItems([])
        }
      }
    }

    loadActionItems()
    return () => controller.abort()
  }, [userId])

  // Adaptive Logic
  const activeContext = useMemo(() => {
    if (demoContext) return demoContext
    
    const now = currentTime
    const hour = now.getHours()
    
    // 1. Pre-meeting check (High Priority)
    if (eventSummary) {
      const nextMeeting = eventSummary.today.find(e => {
        if (e.tagTheme !== 'meeting') return false
        const diffMs = e.start.getTime() - now.getTime()
        const diffMins = diffMs / (1000 * 60)
        return diffMins > 0 && diffMins <= 60
      })
      if (nextMeeting) return 'PRE_MEETING'
    }

    // 2. Post-meeting check (High Priority)
    if (latestCallTime) {
      const diffMs = now.getTime() - latestCallTime.getTime()
      const diffMins = diffMs / (1000 * 60)
      if (diffMins >= 0 && diffMins <= 30) {
        return 'POST_MEETING'
      }
    }
    
    // 3. Morning check (Medium Priority)
    if (hour >= 4 && hour < 11) return 'MORNING'
    
    // 4. End of Day check (Medium Priority)
    if (hour >= 20 && hour < 24) return 'END_OF_DAY'
    
    return 'DEFAULT'
  }, [demoContext, eventSummary, latestCallTime, currentTime])

  const activeEvent = useMemo(() => {
    if (!eventSummary) return null
    const now = currentTime
    
    if (activeContext === 'PRE_MEETING') {
      return eventSummary.today.find(e => {
        if (e.tagTheme !== 'meeting') return false
        const diffMs = e.start.getTime() - now.getTime()
        const diffMins = diffMs / (1000 * 60)
        return diffMins > 0 && diffMins <= 60
      })
    }
    return null
  }, [activeContext, eventSummary, currentTime])

  useEffect(() => {
    const controller = new AbortController()
    const loadActiveEventDetails = async () => {
      if (!activeEvent) {
        setActiveEventDetails(null)
        return
      }

      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) return

        // 1. Fetch clients for the calendar event
        let clients: ClientRecord[] = []
        try {
          clients = await fetchCalendarEventClients(activeEvent.id, ensuredId)
        } catch {
          // non-fatal — proceed with empty clients list
        }

        // 2. Identify potential related conversations
        const relatedIds = new Set<string>()
        if (activeEvent.conversation_id) {
          relatedIds.add(activeEvent.conversation_id)
        }

        // Fetch conversations for each client
        if (clients.length > 0) {
          const allClientConvs = await Promise.all(
            clients.map((client) => listConversations(ensuredId, { clientId: client.id, signal: controller.signal }))
          )
          allClientConvs.flat().forEach(c => relatedIds.add(c.id))
        }

        // 3. Fetch full details for the top related conversations
        const topIds = Array.from(relatedIds).slice(0, 5) // Limit to avoid too many requests
        const details = await Promise.all(
          topIds.map((id) => getConversation(id, ensuredId, controller.signal))
        )
        const fullConversations: Conversation[] = details.flatMap((detail) => {
          const conv = detail.conversation as Conversation
          const attachedClients = detail.clients || []
          const attachedDates = detail.dates || []

          // Verify "Neither is attached" rule
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
          const hasSharedClient = attachedClients.some((ac: any) => clients.some(c => c.id === ac.id))
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
          const isAttachedMeeting = attachedDates.some((ad: any) => ad.id === activeEvent.id)
          const isExplicitLink = conv?.id === activeEvent.conversation_id

          if (conv && (hasSharedClient || isAttachedMeeting || isExplicitLink)) {
            return [{
              ...conv,
              clients: attachedClients,
              dates: attachedDates,
              action_items: detail.action_items || detail.actionItems
            }]
          }
          return []
        })

        setActiveEventDetails({ 
          clients, 
          conversations: fullConversations.sort((a, b) => {
            const aTime = a.date_created ? new Date(a.date_created).getTime() : 0
            const bTime = b.date_created ? new Date(b.date_created).getTime() : 0
            return bTime - aTime
          }).slice(0, 3) 
        })
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to load active event details', error)
        }
      }
    }

    loadActiveEventDetails()
    return () => controller.abort()
  }, [activeEvent, userId])

  useLayoutEffect(() => {
    if (!name || !typedNameRef.current) return
    const element = typedNameRef.current
    const updateWidth = () => {
      const width = element.scrollWidth
      if (width) {
        element.style.setProperty('--name-width', `${width + 4}px`)
      }
    }
    updateWidth()
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (typedNameRef.current === element) {
          updateWidth()
        }
      })
    }
  }, [name, nameCharCount])

  const showSimulator = import.meta.env.VITE_SHOW_SIMULATOR === 'true'

  if (!isStatsReady) {
    return (
      <div className="home-view-container" style={{ justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Loading />
      </div>
    )
  }

  return (
    <div className="home-view-container">
      {showSimulator && (
        <div className="demo-mode-bar">
          <span className="demo-mode-label">⏰ Simulator:</span>
          <div className="demo-mode-buttons" style={{ display: 'flex', gap: 8 }}>
            {(['MORNING', 'PRE_MEETING', 'POST_MEETING', 'END_OF_DAY', 'DEFAULT'] as DashboardContext[]).map(ctx => (
              <button
                type="button"
                key={ctx}
                className={`demo-mode-btn ${activeContext === ctx ? 'active' : ''}`}
                onClick={() => setDemoContext(ctx === demoContext ? null : ctx)}
              >
                {ctx.replace('_', ' ')}
              </button>
            ))}
            <button
              className="demo-mode-btn"
              onClick={() => {
                localStorage.setItem(ONBOARDING_STATUS_KEY, 'pending')
                window.location.reload()
              }}
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              Onboarding
            </button>
          </div>
        </div>
      )}

      {activeContext === 'MORNING' && (
        <div className="home-page">
          <MorningView 
            firstName={firstName} 
            eventSummary={eventSummary} 
            latestActionItems={latestActionItems?.filter(item => {
              if (item.done) return false
              const dueDate = utcStringToLocalDate(item.due_date || item.dueDate)
              if (!dueDate) return false
              const now = new Date()
              return dueDate.toDateString() === now.toDateString()
            }) || []} 
            recentDrafts={recentDrafts}
          />
        </div>
      )}

      {activeContext === 'PRE_MEETING' && (
        <div className="home-page">
          <PreMeetingView activeEvent={activeEvent} details={activeEventDetails} />
        </div>
      )}

      {activeContext === 'POST_MEETING' && (
        <div className="home-page">
          <PostMeetingView 
            activeEvent={activeEvent} 
            latestConversation={latestConversation}
          />
        </div>
      )}

      {activeContext === 'END_OF_DAY' && (
        <div className="home-page">
          <EndOfDayView 
            firstName={firstName} 
            eventSummary={eventSummary} 
            latestActionItems={latestActionItems} 
            clientTotals={clientTotals} 
          />
        </div>
      )}

      {activeContext === 'DEFAULT' && (
        <div className="home-page">
          <DefaultView 
            name={name}
            nameStyle={nameStyle}
            typedNameRef={typedNameRef}
            latestActionItems={latestActionItems}
            newContacts={newContacts}
            eventSummary={eventSummary}
            clientTotals={clientTotals}
            isStatsReady={isStatsReady}
            showWeeklyCallGradient={showWeeklyCallGradient}
            weeklyCallDisplay={weeklyCallDisplay}
            utcStringToLocalDate={utcStringToLocalDate}
          />
        </div>
      )}
    </div>
  )
}

export default HomeView
